
import React, { useEffect, useState } from 'react';
import { Card, Button, Badge, Input, Select } from '../components/UI';
import { apiRequest } from '../utils/api';
import { Edit2, Trash2, CheckCircle, X, Save, Plus, Calendar, Flag, Power, CheckSquare } from 'lucide-react';
import { Season, SeasonStatus } from '../types';
import * as ReactRouterDOM from 'react-router-dom';
import { useLeague } from '../components/LeagueContext';
import { PageHero } from '../components/PageHero';

const { Link } = ReactRouterDOM;

interface SeasonWithCounts extends Season {
    event_count: number;
    team_count: number;
    is_active: boolean;
}

export const SeasonList: React.FC = () => {
    const { refreshData } = useLeague();
    const [seasons, setSeasons] = useState<SeasonWithCounts[]>([]);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editForm, setEditForm] = useState<Partial<Season>>({});

    const loadSeasons = () => {
        apiRequest<SeasonWithCounts[]>('seasons.php?all=true').then(data => {
            setSeasons(Array.isArray(data) ? data : []);
        }).catch(err => {
            console.error("Failed to load seasons", err);
        });
    };

    useEffect(() => {
        loadSeasons();
    }, []);

    const handleDelete = async (id: string) => {
        if (!confirm("GEFAHRENBEREICH: Beim Löschen werden alle Rennen, Ergebnisse und Grid-Zuordnungen dieser Saison unwiderruflich entfernt. Wirklich fortfahren?")) return;
        try {
            await apiRequest(`seasons.php?id=${id}`, 'DELETE');
            loadSeasons();
        } catch (e: any) {
            alert(e.message);
        }
    };

    const handleActivate = async (id: string) => {
        if (!confirm("Diese Saison wird sofort aktiv. Dashboard, Tabellen und Fahrerfeld wechseln unmittelbar auf diese Saison. Fortfahren?")) return;
        try {
            await apiRequest('seasons.php', 'POST', { action: 'activate', id });
            await refreshData();
            loadSeasons();
        } catch (e: any) {
            alert(e.message);
        }
    };

    const handleFinish = async (id: string) => {
        if (!confirm("⚠️ WARNUNG: Möchtest du diese Saison wirklich beenden?\n\nDies setzt den Status auf 'FINISHED' und beendet SOFORT alle laufenden Driver of the Day Votings!")) return;
        try {
            await apiRequest('seasons.php', 'POST', { action: 'finish', id });
            await refreshData();
            loadSeasons();
        } catch (e: any) {
            alert(e.message);
        }
    };

    const startEdit = (season: SeasonWithCounts) => {
        setEditingId(season.id);
        setEditForm({ name: season.name, year: season.year, status: season.status });
    };

    const saveEdit = async () => {
        if (!editingId) return;
        try {
            await apiRequest('seasons.php', 'PUT', { id: editingId, ...editForm });
            setEditingId(null);
            await refreshData();
            loadSeasons();
        } catch (e: any) {
            alert(e.message);
        }
    };

    return (
        <div className="mx-auto max-w-7xl space-y-6 animate-fade-in pb-20">
            <PageHero
                eyebrow="Season Control"
                title="Saisons"
                description="Saisonstatus, Kalender und Aktivierung aller Liga-Spielzeiten zentral und nachvollziehbar verwalten."
                icon={Flag}
                accent="#e10600"
                stats={[
                    { label: 'Saisons', value: seasons.length },
                    { label: 'Aktiv', value: seasons.filter(season => season.is_active).length },
                    { label: 'Beendet', value: seasons.filter(season => season.status === SeasonStatus.FINISHED).length },
                ]}
                actions={<Link to="/admin/wizard"><Button icon={Plus}>Neue Saison</Button></Link>}
            />

            <div className="grid grid-cols-1 gap-4">
                {seasons.length === 0 && <div className="rounded-[1.75rem] border border-dashed border-white/[0.075] p-10 text-center text-gray-500">Noch keine Saison vorhanden. Starte den Assistenten.</div>}

                {seasons.map(season => {
                    const isActive = season.is_active && season.status === SeasonStatus.PUBLISHED;
                    return (
                        <Card key={season.id} className={`overflow-hidden rounded-[1.75rem] border bg-gradient-to-br from-[#171a24] to-[#0e1017] shadow-[0_22px_60px_rgba(0,0,0,0.2)] transition-all ${isActive ? 'border-f1-red/35' : 'border-white/[0.075] opacity-85 hover:opacity-100'}`}>
                            {editingId === season.id ? (
                                <div className="flex flex-col gap-4">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <Input 
                                            label="Saisonname" 
                                            value={editForm.name} 
                                            onChange={e => setEditForm({...editForm, name: e.target.value})}
                                        />
                                        <Input 
                                            label="Jahr" 
                                            type="number"
                                            value={editForm.year} 
                                            onChange={e => setEditForm({...editForm, year: parseInt(e.target.value)})}
                                        />
                                        <Select 
                                            label="Status"
                                            value={editForm.status}
                                            onChange={e => setEditForm({...editForm, status: e.target.value as SeasonStatus})}
                                            options={[
                                                {label: 'Entwurf (unsichtbar)', value: SeasonStatus.DRAFT},
                                                {label: 'Veröffentlicht', value: SeasonStatus.PUBLISHED},
                                                {label: 'Beendet', value: SeasonStatus.FINISHED},
                                            ]}
                                        />
                                    </div>
                                    <div className="flex justify-end gap-2">
                                        <Button variant="ghost" size="sm" onClick={() => setEditingId(null)}><X className="mr-1 w-4 h-4"/> Abbrechen</Button>
                                        <Button size="sm" onClick={saveEdit}><Save className="mr-1 w-4 h-4"/> Speichern</Button>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-1">
                                            <h3 className="text-xl font-bold uppercase italic text-white">{season.name}</h3>
                                            {isActive && <Badge color="red" className="flex items-center gap-1 shadow-lg shadow-red-900/50"><CheckCircle size={12}/> AKTIV</Badge>}
                                            <Badge color={season.status === 'PUBLISHED' ? 'green' : season.status === 'FINISHED' ? 'gray' : 'yellow'}>
                                                {season.status}
                                            </Badge>
                                        </div>
                                        <div className="text-sm text-gray-400 flex items-center gap-4">
                                            <span>Saison {season.year}</span>
                                            <span className="flex items-center gap-1"><Flag size={14}/> {season.event_count} Rennen</span>
                                            <span className="flex items-center gap-1"><Calendar size={14}/> {season.team_count} Teams</span>
                                        </div>
                                    </div>
                                    
                                    <div className="flex items-center gap-2">
                                        {season.status !== 'FINISHED' && (
                                            <Button variant="secondary" size="sm" onClick={() => handleFinish(season.id)} className="bg-green-800/50 hover:bg-green-700 text-green-100 border border-green-600">
                                                <CheckSquare className="w-4 h-4 mr-2"/> Beenden
                                            </Button>
                                        )}
                                        {!isActive && season.status !== SeasonStatus.FINISHED && (
                                            <Button variant="primary" size="sm" onClick={() => handleActivate(season.id)} className="bg-gray-700 hover:bg-f1-red border border-gray-600">
                                                <Power className="w-4 h-4 mr-2" /> Aktivieren
                                            </Button>
                                        )}
                                        
                                        {/* LINK TO NEW CALENDAR PAGE */}
                                        <Link to={`/admin/seasons/${season.id}/calendar`}>
                                            <Button variant="secondary" size="sm">
                                                <Calendar className="w-4 h-4 mr-2" /> Kalender
                                            </Button>
                                        </Link>

                                        <Button variant="secondary" size="sm" onClick={() => startEdit(season)}>
                                            <Edit2 className="w-4 h-4" />
                                        </Button>
                                        <Button variant="danger" size="sm" onClick={() => handleDelete(season.id)}>
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </Card>
                    );
                })}
            </div>
        </div>
    );
};
